/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps, ReactNode } from 'react'
import { forwardRef, useId } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { Controller, useFormContext, useFormState } from 'react-hook-form'

import { cn, mergeRefs } from '../../../lib/utils'
import { Checkbox } from '../../ui/checkbox'
import { Field, FieldDescription, FieldError } from '../../ui/field'

export type CheckBoxFormFieldProps = {
  checkboxProps?: ComponentProps<typeof Checkbox>
  children?: ReactNode
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  name: string
  required?: boolean
  rules?: RegisterOptions
}

export const CheckBoxFormField = forwardRef<HTMLInputElement, CheckBoxFormFieldProps>((props, forwardedRef) => {
  const { control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const checkboxId = useId()

  const error = errors[props.name]
  const invalid = !!error

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => (
          <label
            className={cn(
              'flex cursor-pointer items-start gap-2 text-base leading-snug',
              props.disabled && 'cursor-not-allowed opacity-50'
            )}
            htmlFor={checkboxId}
          >
            <Checkbox
              aria-invalid={invalid}
              checked={!!field.value}
              disabled={props.disabled}
              id={checkboxId}
              inputRef={mergeRefs(field.ref, forwardedRef)}
              name={field.name}
              onBlur={field.onBlur}
              onCheckedChange={(checked) => field.onChange(checked)}
              required={props.required}
              {...props.checkboxProps}
            />
            <span>{props.children ?? props.label}</span>
          </label>
        )}
        rules={props.rules}
      />
      {props.helperText !== undefined ? (
        <FieldDescription className="text-md">{props.helperText}</FieldDescription>
      ) : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

CheckBoxFormField.displayName = 'CheckBoxFormField'
